import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEntities } from "@hooks/useEntities";
import { useEntityIdsWithAccrualLedger } from "@api/ledger.api";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import { PaginatedTransactionsTableContextProvider } from "@components/transactions/PaginatedTransactionsTableContextProvider";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import {
    TransactionsBulkActionsContextProvider,
    TransactionsBulkActionsContextProviderProps,
} from "@components/transactions/TransactionsBulkActions/TransactionsBulkActionsContextProvider";
import { BulkUpdateMode } from "@components/transactions/TransactionsBulkActions/lib";
import {
    ACCRUAL_REVIEW_QUERY_KEY,
    invalidateTransactionsQueries,
} from "@components/transactions/useTransactionsQuery";
import { AccrualReviewPage } from "./AccrualReviewPage";
import { createAccrualReviewDefaultView } from "./AccrualReviewDefaultView";

export const AccrualReviewPageContainer: React.FC = () => {
    const [page, setPage] = useState(1);
    const queryClient = useQueryClient();

    useEffect(() => {
        queryClient.invalidateQueries({
            queryKey: [ACCRUAL_REVIEW_QUERY_KEY],
        });
    }, [queryClient]);

    const entities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const entityIds = useMemo(() => entities.map((e) => e.id), [entities]);
    const accrualEntityIds = useEntityIdsWithAccrualLedger(entityIds);

    const accrualEntityIdsArray = useMemo(
        () => [...accrualEntityIds],
        [accrualEntityIds],
    );

    const defaultView = useMemo(
        () => createAccrualReviewDefaultView(accrualEntityIdsArray),
        [accrualEntityIdsArray],
    );

    return (
        <PaginatedTransactionsTableContextProvider
            paginationMode={{ type: "pages", page, setPage }}
            defaultView={defaultView}
            cacheKey={
                WORKSPACE_USER_CACHE_KEYS.ACCRUAL_REVIEW_PAGE_VIEW_CACHE_KEY
            }
            disableSimilarTransactionsUpdate
            includeAccrualMatchDetails
            queryKeyPrefix={ACCRUAL_REVIEW_QUERY_KEY}
        >
            <AccrualReviewPageWithBulkActions
                accrualEntityIds={accrualEntityIds}
            />
        </PaginatedTransactionsTableContextProvider>
    );
};

interface AccrualReviewPageWithBulkActionsProps {
    accrualEntityIds: Set<number>;
}

const AccrualReviewPageWithBulkActions: React.FC<
    AccrualReviewPageWithBulkActionsProps
> = ({ accrualEntityIds }) => {
    const { transactionCount, filters } = useContext(
        PaginatedTransactionsDataContext,
    );
    const { updateMany, setPage } = useContext(
        PaginatedTransactionsActionsContext,
    );

    const onBulkUpdated: TransactionsBulkActionsContextProviderProps["onUpdated"] =
        useCallback(
            (updatedTransactions, { mode }) => {
                if (mode === BulkUpdateMode.SELECTED) {
                    updateMany(updatedTransactions);
                } else {
                    void invalidateTransactionsQueries();
                    setPage(1);
                }
            },
            [setPage, updateMany],
        );

    return (
        <TransactionsBulkActionsContextProvider
            currentFilters={filters}
            onUpdated={onBulkUpdated}
            totalTransactions={transactionCount}
        >
            <AccrualReviewPage accrualEntityIds={accrualEntityIds} />
        </TransactionsBulkActionsContextProvider>
    );
};
